import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  isPlatformServer,
  DOCUMENT,
  isPlatformBrowser,
  NgClass,
  NgIf,
  NgStyle,
} from '@angular/common';
import { finalize, Observable, Subscription } from 'rxjs';
import { SwiperContainer } from 'swiper/element';
import { SwiperOptions } from 'swiper/types';
import { ApiService } from '../../../services/api.service';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { SectionHeadingComponent } from '../section-heading/section-heading.component';
import { SectionDescriptionComponent } from '../section-description/section-description.component';
import { CardChipsComponent } from '../card/card-chips/card-chips.component';
import { CardSkeletonLoaderComponent } from '../../utility/card-skeleton-loader/card-skeleton-loader.component';
import { routes } from '../../../constants/routes';
import { CategoryTypes } from '../../../models/category-types.model';
import { CardType } from '../../../models/card-type.model';
import { CouldntLoadDataComponent } from '../couldnt-load-data/couldnt-load-data.component';
import { NoRecordFoundComponent } from '../no-record-found/no-record-found.component';
import { apiErrorHandler } from '../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../models/api-error-handler.model';
import { ToasterService } from '../../../services/toaster.service';
import { CompetitionCardComponent } from '../../utility/competition-card/competition-card.component';
import { Competition } from '../../../models/competition.model';

@Component({
  selector: 'app-home-competitions',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    SectionContainerComponent,
    SectionHeadingComponent,
    SectionDescriptionComponent,
    CompetitionCardComponent,
    CardChipsComponent,
    NgClass,
    CardSkeletonLoaderComponent,
    CouldntLoadDataComponent,
    NoRecordFoundComponent,
    NgStyle,
  ],
  templateUrl: './home-competitions.component.html',
  styleUrl: './home-competitions.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HomeCompetitionsComponent implements OnInit, OnDestroy, ApiErrorHandler {
  selectedCategory!: CategoryTypes;
  isCompetitionsLoading!: boolean;
  private isSwiperInitialize = false;
  competitions?: Competition[];
  readonly CategoryTypes = CategoryTypes;
  readonly CardType = CardType;
  readonly routes = routes;
  private fetchCompetitionsSubs!: Subscription;
  public isErrorFetchingCompetitions: boolean = false;
  description = 'Solve real-world challenges and win big.';

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: string,
    public toaster: ToasterService,
    private apiService: ApiService
  ) {}

  ngOnInit() {
    this.selectedCategory = this.CategoryTypes.trending;
    this.fetchCompetitions(this.selectedCategory);
  }

  public fetchCompetitions(filterType: string) {
    this.competitions = [];
    this.isCompetitionsLoading = true;
    let fetchSelectedCategoryCompetition$: Observable<any>;
    fetchSelectedCategoryCompetition$ = this.apiService.fetchCompetitions(1, null, 12, filterType);

    this.fetchCompetitionsSubs = fetchSelectedCategoryCompetition$
      .pipe(
        finalize(() => {
          this.isCompetitionsLoading = false;
        })
      )
      .subscribe({
        next: (resp) => {
          if (filterType === this.CategoryTypes.new) {
            this.competitions = resp.data.data;
          } else {
            this.competitions = resp.data.data;
          }

          if (
            this.competitions?.length &&
            !this.isSwiperInitialize &&
            isPlatformBrowser(this.platformId)
          ) {
            requestAnimationFrame(() => {
              this.initSwiperCarousel();
            });
          } else {
            const swiperContainer: SwiperContainer =
              this.document.querySelector('#competition-swiper-container')!;
            swiperContainer?.swiper?.update();
          }
        },
        error: (error) => {
          this.isErrorFetchingCompetitions = true;
          this.competitions = [];
          apiErrorHandler.call(this, error);
        },
      });
  }

  private initSwiperCarousel() {
    this.isSwiperInitialize = true;
    if (isPlatformServer(this.platformId)) {
      return;
    }

    const swiperProps: SwiperOptions = {
      slidesPerView: 1.2,
      speed: 500,
      spaceBetween: 18,
      navigation: {
        prevEl: '.hero-carousel-arrow--prev',
        nextEl: '.hero-carousel-arrow--next',
      },
      pagination: {
        el: '.competition-swiper-pagination ',
        clickable: true,
      },
      loop: true,
      autoplay: {
        delay: 10000,
        stopOnLastSlide: false,
      },
      breakpoints: {
        1300: {
          slidesPerView: 4,
          spaceBetween: 18,
        },
        1000: {
          slidesPerView: 3,
          spaceBetween: 18,
        },
        768: {
          slidesPerView: 2,
          spaceBetween: 18,
        },
      },
    };

    const swiperContainer: SwiperContainer = this.document.querySelector(
      '#competition-swiper-container'
    )!;
    Object.assign(swiperContainer, swiperProps);
    swiperContainer.initialize();
  }

  ngOnDestroy() {
    this.fetchCompetitionsSubs.unsubscribe();
  }
}
